import { useEffect, useState } from 'react'

interface GeoCoords {
  lat: number
  lng: number
}

interface GeolocationState {
  coords: GeoCoords | null
  isLoading: boolean
  error: string | null
  isGranted: boolean
}

/**
 * Browser geolocation hook — requests position once on mount.
 * Fails silently if denied/unavailable (coords stays null).
 */
export function useGeolocation(): GeolocationState {
  const [state, setState] = useState<GeolocationState>({
    coords: null,
    isLoading: true,
    error: null,
    isGranted: false,
  })

  useEffect(() => {
    if (!navigator.geolocation) {
      setState({ coords: null, isLoading: false, error: 'not-supported', isGranted: false })
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setState({
          coords: { lat: position.coords.latitude, lng: position.coords.longitude },
          isLoading: false,
          error: null,
          isGranted: true,
        })
      },
      (err) => {
        setState({
          coords: null,
          isLoading: false,
          error: err.message,
          isGranted: false,
        })
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
      }
    )
  }, [])

  return state
}
